import { canv } from "@/canv/global";
import type { Group } from "@/group";
import { getGroupsWithOverlapped } from "@/utils";
import { selectionManager } from "./SelectionManager";
import type { Ghost, PT } from "./types";
import { createDuplicateGroup } from "./utils";

class CopyManager {
    startCopying() {
        const selected = canv.canvasController.selected;

        if (!selected.length) return;

        const pt = canv.actionManager.getActionPoint();
        this.copyStartPt = { x: pt.x, y: pt.y };

        const selProps = canv.canvasController.getSelectedProps();
        this.copyGrabOffset = { dx: pt.x - selProps.startX, dy: pt.y - selProps.startY };

        const { groups, ghosts } = getGroupsWithOverlapped(
            selected,
            selProps.startX,
            selProps.startY,
            true,
        );
        this.copyGroupsWithOverlapped = groups;
        this.copyGhosts = ghosts;
    }

    stopCopying() {
        const pt = canv.actionManager.getActionPoint();
        const MIN_DRAG_PX = 3;
        const minDrag = canv.actionManager.getZoomConstant(MIN_DRAG_PX);
        const moved = this.copyStartPt
            ? Math.hypot(pt.x - this.copyStartPt.x, pt.y - this.copyStartPt.y) >= minDrag
            : false;

        if (!moved) {
            canv.toolBox.TryGetToolByName("cursorTool").turnMode("");
            return;
        }

        const selProps = canv.canvasController.getSelectedProps();
        const targetX = pt.x - this.copyGrabOffset.dx;
        const targetY = pt.y - this.copyGrabOffset.dy;

        canv.eventManager.openRecord();
        const created: Group[] = [];

        for (let i = 0; i < this.copyGroupsWithOverlapped.length; i++) {
            const group = this.copyGroupsWithOverlapped[i];

            const relDx = group.startX - selProps.startX;
            const relDy = group.startY - selProps.startY;

            const ghost = createDuplicateGroup(group, false) as Group;
            const dx = targetX + relDx - ghost.startX;
            const dy = targetY + relDy - ghost.startY;

            if (dx || dy) ghost.translateGroup(dx, dy);

            canv.canvasController.addGroup(ghost);
            canv.eventManager.createBuildEvent("create", ghost);
            created.push(ghost);
        }

        canv.eventManager.closeRecord();
        selectionManager.setSelected(created);

        canv.toolBox.TryGetToolByName("cursorTool").turnMode("");
    }

    drawCopyingShadow() {
        const copyGhosts = this.copyGhosts;
        const grab = this.copyGrabOffset;
        if (!copyGhosts.length) return;

        const pt = canv.actionManager.getActionPoint();
        const baseX = pt.x - grab.dx;
        const baseY = pt.y - grab.dy;

        const ctx = canv.ctx;
        ctx.save();

        ctx.globalAlpha = 0.3;
        ctx.lineWidth = canv.actionManager.getZoomConstant(1);
        ctx.setLineDash([6, 6]);
        ctx.fillStyle = "#0c8de9";
        ctx.strokeStyle = "#0c8de9";

        for (let i = 0; i < copyGhosts.length; i++) {
            ctx.fillRect(
                baseX + copyGhosts[i].dx,
                baseY + copyGhosts[i].dy,
                copyGhosts[i].w,
                copyGhosts[i].h,
            );
            ctx.strokeRect(
                baseX + copyGhosts[i].dx,
                baseY + copyGhosts[i].dy,
                copyGhosts[i].w,
                copyGhosts[i].h,
            );
        }

        ctx.setLineDash([]);
        ctx.restore();
    }

    reset() {
        this.copyGroupsWithOverlapped = [];
        this.copyGhosts = [];
        this.copyGrabOffset = { dx: 0, dy: 0 };
        this.copyStartPt = null;
    }

    copyGroupsWithOverlapped: Group[] = [];
    copyGhosts: Ghost[] = [];
    copyGrabOffset = { dx: 0, dy: 0 };
    copyStartPt: PT | null = null;
}

export const copyManager = new CopyManager();
